import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, ArrowLeft, ChevronRight, Briefcase, Send, FileSignature,
  Plus, CheckCircle2, MessageSquare, Repeat, FileText, CreditCard, Copy, Receipt,
} from 'lucide-react';
import { adminApi, STATE_LABELS, TRANSITIONS } from '@/components/admin/adminApi';

const STATE_TONE = {
  draft: 'bg-slate-600/40 text-slate-300', requirements_submitted: 'bg-blue-500/15 text-blue-300',
  scoping: 'bg-amber-500/15 text-amber-300', scope_approved: 'bg-blue-500/15 text-blue-300',
  agreement_pending: 'bg-amber-500/15 text-amber-300', agreement_accepted: 'bg-brand-orange/15 text-brand-orange',
  subscribed: 'bg-green-500/15 text-green-300', onboarding: 'bg-blue-500/15 text-blue-300',
  in_development: 'bg-blue-500/15 text-blue-300', launched: 'bg-green-500/15 text-green-300',
  ongoing: 'bg-green-500/15 text-green-300', paused: 'bg-slate-600/40 text-slate-300',
  cancelled: 'bg-rose-500/15 text-rose-300', legacy: 'bg-slate-600/40 text-slate-300',
};

const Card = ({ title, icon: Icon, children, action }) => (
  <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
    <div className="flex items-center justify-between mb-3">
      <h4 className="text-xs font-bold text-slate-200 uppercase tracking-wider flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-brand-blue" />}{title}</h4>
      {action}
    </div>
    {children}
  </div>
);

const inputCls = 'w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-blue/40';

const ProjectDetail = ({ projectId, onBack, onChanged }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toState, setToState] = useState('');
  const [scopeSummary, setScopeSummary] = useState('');
  const [scopeDelivs, setScopeDelivs] = useState('');
  const [msTitle, setMsTitle] = useState('');
  const [dlvTitle, setDlvTitle] = useState('');
  const [dlvType, setDlvType] = useState('document');
  const [msg, setMsg] = useState('');
  const [addonAmt, setAddonAmt] = useState({});   // addon_id -> price input
  const [lastPayLink, setLastPayLink] = useState('');

  const load = useCallback(async () => {
    try { const r = await adminApi.projectFull(projectId); setD(r); }
    catch (e) { toast.error('Could not load project'); }
    finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 text-brand-blue animate-spin" /></div>;
  if (!d) return null;
  const { project: p, subscription, scope, agreement, milestones, deliverables, addons, messages, invoices, next_action: na, account } = d;
  const allowed = TRANSITIONS[p.lifecycle_state] || [];

  const run = async (fn, ok) => { setBusy(true); try { await fn(); if (ok) toast.success(ok); await load(); onChanged?.(); } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); } finally { setBusy(false); } };

  const copyPayLink = async (url) => {
    if (!url) return;
    setLastPayLink(url);
    try { await navigator.clipboard.writeText(url); toast.success('Payment link copied to clipboard'); }
    catch { toast.message('Payment link ready', { description: url }); }
  };

  const generateInvoice = async (kind) => {
    if (!subscription) { toast.error('Create a subscription first.'); return; }
    setBusy(true);
    try {
      const res = await adminApi.generateInvoice(subscription.id, kind ? { kind } : {});
      if (res?.checkout_url) { await copyPayLink(res.checkout_url); }
      toast.success(`Invoice ${res?.invoice?.invoice_id || ''} ready${res?.mode === 'checkout_link' ? ' — pay link copied' : ''}`);
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate invoice (PayMongo may be unavailable in preview).');
    } finally { setBusy(false); }
  };

  const billAddon = async (addon, mode) => {
    const amt = parseFloat(addonAmt[addon.id] ?? addon.amount_usd);
    if (!amt || amt <= 0) { toast.error('Enter an add-on price first.'); return; }
    setBusy(true);
    try {
      const res = await adminApi.billAddon(addon.id, { mode, amount_usd: amt });
      if (res?.checkout_url) { await copyPayLink(res.checkout_url); }
      toast.success(mode === 'next_invoice' ? 'Add-on will be billed on the next renewal.' : 'One-time add-on invoice created — pay link copied.');
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not bill add-on.');
    } finally { setBusy(false); }
  };

  const copyInvoiceLink = async (invoiceId) => {
    setBusy(true);
    try { const res = await adminApi.invoicePayLink(invoiceId); await copyPayLink(res?.checkout_url); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not create a pay link (PayMongo may be unavailable in preview).'); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-project-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-3"><ArrowLeft className="w-4 h-4" /> All projects</button>
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <h2 className="text-xl font-bold text-white">{p.name}</h2>
        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-md ${STATE_TONE[p.lifecycle_state] || 'bg-slate-700 text-slate-300'}`} data-testid="admin-project-state">{STATE_LABELS[p.lifecycle_state] || p.lifecycle_state}</span>
      </div>
      <p className="text-sm text-slate-400 mb-1">{account?.email || p.account_email} · Plan: {p.plan_id || '—'} · {p.billing_cycle}</p>
      <p className="text-xs text-slate-500 mb-6">{na?.label}</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Lifecycle */}
        <Card title="Lifecycle" icon={ChevronRight}>
          <div className="flex items-center gap-2">
            <select value={toState} onChange={(e) => setToState(e.target.value)} className={inputCls} data-testid="transition-select">
              <option value="">Move to…</option>
              {allowed.map((s) => <option key={s} value={s}>{STATE_LABELS[s] || s}</option>)}
            </select>
            <button disabled={!toState || busy} onClick={() => run(() => adminApi.transition(projectId, toState), 'Project updated').then(() => setToState(''))} className="px-3 py-2 rounded-lg bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blue-dark disabled:opacity-40 shrink-0" data-testid="transition-apply">Apply</button>
          </div>
          {allowed.length === 0 && <p className="text-xs text-slate-500 mt-2">No further transitions from this state.</p>}
        </Card>

        {/* Requirements */}
        <Card title="Requirements" icon={FileText}>
          {p.requirements && Object.values(p.requirements).some((v) => (v || '').trim()) ? (
            <div className="space-y-1.5 max-h-40 overflow-y-auto text-sm">
              {Object.entries(p.requirements).filter(([, v]) => (v || '').trim()).map(([k, v]) => (
                <p key={k} className="text-slate-300"><span className="text-slate-500">{k}:</span> {v}</p>
              ))}
              <p className="text-[11px] text-slate-500 pt-1">Status: {p.requirements_status || 'none'}</p>
            </div>
          ) : <p className="text-sm text-slate-500">No requirements submitted yet.</p>}
        </Card>

        {/* Scope */}
        <Card title="Scope" icon={FileText}>
          {scope ? (
            <div className="mb-3 text-sm">
              <p className="text-slate-300 whitespace-pre-wrap">{scope.summary}</p>
              <p className="text-[11px] text-slate-500 mt-1">State: {scope.state} · v{scope.version}</p>
            </div>
          ) : <p className="text-sm text-slate-500 mb-3">No scope yet.</p>}
          <textarea value={scopeSummary} onChange={(e) => setScopeSummary(e.target.value)} rows={2} placeholder="Scope summary…" className={`${inputCls} resize-none mb-2`} data-testid="scope-summary-input" />
          <input value={scopeDelivs} onChange={(e) => setScopeDelivs(e.target.value)} placeholder="Deliverables, comma-separated" className={`${inputCls} mb-2`} data-testid="scope-delivs-input" />
          <button disabled={busy || !scopeSummary.trim()} onClick={() => run(() => adminApi.submitScope(projectId, { summary: scopeSummary.trim(), deliverables: scopeDelivs.split(',').map((s) => s.trim()).filter(Boolean) }), 'Scope sent to client').then(() => { setScopeSummary(''); setScopeDelivs(''); })} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blue-dark disabled:opacity-40" data-testid="submit-scope-btn"><Send className="w-3.5 h-3.5" /> Submit scope to client</button>
        </Card>

        {/* Agreement + Subscription + Billing */}
        <Card title="Agreement & Billing" icon={FileSignature}>
          <div className="text-sm text-slate-300 mb-2 space-y-0.5">
            <p>Agreement: <span className="text-slate-200 font-medium">{agreement ? agreement.status : 'none'}</span></p>
            <p>Subscription: {subscription ? (<span className="text-slate-200 font-medium capitalize" data-testid="admin-subscription-status">{subscription.status} · ${subscription.amount_usd || 0}/{subscription.billing_cycle === 'annual' ? 'yr' : 'mo'}</span>) : <span className="text-slate-500">none</span>}</p>
            {subscription?.next_billing_at && <p className="text-[11px] text-slate-500">Next billing: {new Date(subscription.next_billing_at).toLocaleDateString()}</p>}
            {(subscription?.pending_addons || []).length > 0 && <p className="text-[11px] text-amber-300" data-testid="pending-addons-note">{subscription.pending_addons.length} add-on(s) queued for next renewal</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <button disabled={busy} onClick={() => run(() => adminApi.sendAgreement(projectId, { terms_version: 'v1' }), 'Agreement sent')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-orange text-white text-sm font-semibold hover:bg-brand-orange-dark disabled:opacity-40" data-testid="send-agreement-btn"><FileSignature className="w-3.5 h-3.5" /> Send agreement</button>
            {!subscription && p.plan_id && (
              <button disabled={busy} onClick={() => run(() => adminApi.createSubscription(projectId, { plan_id: p.plan_id, billing_cycle: p.billing_cycle || 'monthly' }), 'Subscription created (inactive)')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-600 text-slate-200 text-sm font-semibold hover:bg-slate-700 disabled:opacity-40" data-testid="create-subscription-btn"><Repeat className="w-3.5 h-3.5" /> Create subscription</button>
            )}
            {subscription && (
              <>
                {!subscription.current_period_end ? (
                  <button disabled={busy} onClick={() => generateInvoice('initial')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blue-dark disabled:opacity-40" data-testid="generate-initial-invoice-btn"><CreditCard className="w-3.5 h-3.5" /> Generate first invoice</button>
                ) : (
                  <button disabled={busy} onClick={() => generateInvoice('renewal')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blue-dark disabled:opacity-40" data-testid="generate-renewal-invoice-btn"><CreditCard className="w-3.5 h-3.5" /> Generate renewal invoice</button>
                )}
                {subscription.status !== 'active' && (
                  <button disabled={busy} onClick={() => run(() => adminApi.activateSubscription(subscription.id), 'Subscription activated')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-green-600 text-green-300 text-sm font-semibold hover:bg-green-500/10 disabled:opacity-40" data-testid="activate-subscription-btn"><CheckCircle2 className="w-3.5 h-3.5" /> Activate</button>
                )}
              </>
            )}
          </div>
          {lastPayLink && (
            <div className="mt-2 flex items-center gap-2 bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5" data-testid="last-pay-link">
              <span className="text-[11px] text-slate-400 truncate flex-1">{lastPayLink}</span>
              <button onClick={() => copyPayLink(lastPayLink)} className="text-[11px] text-brand-blue hover:text-brand-blue-dark inline-flex items-center gap-1 shrink-0"><Copy className="w-3 h-3" /> Copy</button>
            </div>
          )}
          <p className="text-[11px] text-slate-500 mt-2">Invoices create a secure PayMongo checkout link. Auto-charge stays off until PayMongo activates it on your account.</p>
        </Card>

        {/* Invoices */}
        <Card title="Invoices" icon={Receipt}>
          {(invoices || []).length === 0 ? <p className="text-sm text-slate-500">No invoices yet.</p> : (
            <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="admin-invoices">
              {invoices.map((inv) => {
                const unpaid = inv.status && !['paid', 'void'].includes(inv.status);
                return (
                  <div key={inv.invoice_id} className="flex items-center justify-between gap-2" data-testid={`admin-invoice-${inv.invoice_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-slate-300 truncate">${(inv.total ?? inv.amount ?? 0).toLocaleString()} <span className="text-[11px] text-slate-500 capitalize">· {(inv.kind || 'invoice').replace(/_/g, ' ')}</span></p>
                      <p className="text-[11px] text-slate-500 truncate">{inv.invoice_id}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded capitalize ${inv.status === 'paid' ? 'bg-green-500/15 text-green-300' : 'bg-slate-700 text-slate-300'}`}>{inv.status}</span>
                      {unpaid && <button disabled={busy} onClick={() => copyInvoiceLink(inv.invoice_id)} className="text-[11px] px-2 py-1 rounded bg-brand-blue/15 text-brand-blue hover:bg-brand-blue/25 inline-flex items-center gap-1 disabled:opacity-40" data-testid={`copy-pay-link-${inv.invoice_id}`}><Copy className="w-3 h-3" /> Pay link</button>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Milestones */}
        <Card title="Milestones" icon={CheckCircle2}>
          <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
            {(milestones || []).length === 0 ? <p className="text-sm text-slate-500">None yet.</p> : milestones.map((m) => (
              <div key={m.id} className="flex items-center gap-2" data-testid={`admin-milestone-${m.id}`}>
                <span className="text-sm text-slate-300 flex-1 truncate">{m.title}</span>
                <select value={m.status} onChange={(e) => run(() => adminApi.updateMilestone(m.id, { status: e.target.value }), 'Milestone updated')} className="px-2 py-1 rounded bg-slate-900 border border-slate-700 text-xs text-white">
                  {['pending', 'in_progress', 'done', 'blocked'].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input value={msTitle} onChange={(e) => setMsTitle(e.target.value)} placeholder="New milestone title" className={inputCls} data-testid="milestone-title-input" />
            <button disabled={busy || !msTitle.trim()} onClick={() => run(() => adminApi.createMilestone(projectId, { title: msTitle.trim(), order_index: (milestones || []).length }), 'Milestone added').then(() => setMsTitle(''))} className="px-3 py-2 rounded-lg bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blue-dark disabled:opacity-40 shrink-0" data-testid="add-milestone-btn"><Plus className="w-4 h-4" /></button>
          </div>
        </Card>

        {/* Deliverables */}
        <Card title="Deliverables" icon={Plus}>
          <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
            {(deliverables || []).length === 0 ? <p className="text-sm text-slate-500">None yet.</p> : deliverables.map((dl) => (
              <div key={dl.id} className="flex items-center justify-between" data-testid={`admin-deliverable-${dl.id}`}>
                <span className="text-sm text-slate-300 truncate">{dl.title}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${dl.status === 'approved' ? 'bg-green-500/15 text-green-300' : dl.status === 'revision' ? 'bg-brand-orange/15 text-brand-orange' : 'bg-slate-700 text-slate-400'}`}>{dl.status}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input value={dlvTitle} onChange={(e) => setDlvTitle(e.target.value)} placeholder="Deliverable title" className={inputCls} data-testid="deliverable-title-input" />
            <select value={dlvType} onChange={(e) => setDlvType(e.target.value)} className="px-2 py-2 rounded-lg bg-slate-900 border border-slate-700 text-xs text-white shrink-0">
              {['document', 'design', 'build', 'link'].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <button disabled={busy || !dlvTitle.trim()} onClick={() => run(() => adminApi.createDeliverable(projectId, { title: dlvTitle.trim(), type: dlvType }), 'Deliverable shared').then(() => setDlvTitle(''))} className="px-3 py-2 rounded-lg bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blue-dark disabled:opacity-40 shrink-0" data-testid="add-deliverable-btn"><Plus className="w-4 h-4" /></button>
          </div>
        </Card>

        {/* Add-ons */}
        <Card title="Add-on Requests" icon={Plus}>
          {(addons || []).length === 0 ? <p className="text-sm text-slate-500">None.</p> : (
            <div className="space-y-3">
              {addons.map((a) => (
                <div key={a.id} className="rounded-lg border border-slate-700 p-2.5" data-testid={`admin-addon-${a.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0"><p className="text-sm text-slate-300 truncate">{a.name}</p>{a.description && <p className="text-[11px] text-slate-500 truncate">{a.description}</p>}</div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-700 text-slate-300 capitalize shrink-0">{a.status}</span>
                  </div>
                  {!['active', 'cancelled'].includes(a.status) && (
                    <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">$</span>
                        <input value={addonAmt[a.id] ?? (a.amount_usd ?? '')} onChange={(e) => setAddonAmt((m) => ({ ...m, [a.id]: e.target.value }))} placeholder="Price" type="number" min="0" className="w-24 pl-5 pr-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs text-white focus:outline-none focus:ring-2 focus:ring-brand-blue/40" data-testid={`addon-amount-${a.id}`} />
                      </div>
                      <button disabled={busy} onClick={() => billAddon(a, 'one_time')} className="text-[11px] px-2 py-1.5 rounded bg-brand-orange/15 text-brand-orange hover:bg-brand-orange/25 disabled:opacity-40" data-testid={`bill-addon-onetime-${a.id}`}>Invoice now</button>
                      <button disabled={busy} onClick={() => billAddon(a, 'next_invoice')} className="text-[11px] px-2 py-1.5 rounded bg-brand-blue/15 text-brand-blue hover:bg-brand-blue/25 disabled:opacity-40" data-testid={`bill-addon-roll-${a.id}`}>Roll into renewal</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Messages */}
        <Card title="Messages" icon={MessageSquare}>
          <div className="space-y-2 mb-3 max-h-40 overflow-y-auto" data-testid="admin-messages">
            {(messages || []).length === 0 ? <p className="text-sm text-slate-500">No messages.</p> : messages.map((m) => (
              <div key={m.id} className={`text-sm ${m.sender === 'admin' ? 'text-brand-blue' : 'text-slate-300'}`}><span className="text-[11px] text-slate-500">{m.sender_name}:</span> {m.text}</div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Reply to client…" className={inputCls} data-testid="admin-message-input" />
            <button disabled={busy || !msg.trim()} onClick={() => run(() => adminApi.postMessage(projectId, msg.trim()), null).then(() => setMsg(''))} className="px-3 py-2 rounded-lg bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blue-dark disabled:opacity-40 shrink-0" data-testid="admin-send-message-btn"><Send className="w-4 h-4" /></button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export const ProjectPipelineView = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('active'); // active | all

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await adminApi.projects(); setProjects(r.projects || []); }
    catch (e) { toast.error('Could not load projects'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (selected) return <ProjectPipelineWrapper><ProjectDetail projectId={selected} onBack={() => { setSelected(null); load(); }} onChanged={load} /></ProjectPipelineWrapper>;

  const shown = projects.filter((p) => (filter === 'all' ? true : p.lifecycle_state !== 'legacy' && p.lifecycle_state !== 'cancelled'));

  return (
    <ProjectPipelineWrapper>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold text-white flex items-center gap-2"><Briefcase className="w-5 h-5 text-brand-blue" /> Project Pipeline</h2>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-700 overflow-hidden">
            {['active', 'all'].map((f) => (
              <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 text-xs font-semibold capitalize ${filter === f ? 'bg-brand-blue text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`} data-testid={`pipeline-filter-${f}`}>{f}</button>
            ))}
          </div>
          <button onClick={load} className="p-2 rounded-lg border border-slate-700 text-slate-400 hover:text-white" data-testid="pipeline-refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {loading ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 text-brand-blue animate-spin" /></div> : shown.length === 0 ? (
        <div className="text-center py-16 bg-slate-800 border border-slate-700 rounded-xl"><p className="text-slate-400">No projects yet. Convert a qualified lead or wait for client sign-ups.</p></div>
      ) : (
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden" data-testid="admin-project-list">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-700 text-slate-400 text-left">
              <th className="px-4 py-3 font-semibold">Project</th><th className="px-4 py-3 font-semibold">Client</th>
              <th className="px-4 py-3 font-semibold">Plan</th><th className="px-4 py-3 font-semibold">Stage</th><th className="px-4 py-3" />
            </tr></thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id} className="border-b border-slate-700/60 hover:bg-slate-700/30 cursor-pointer" onClick={() => setSelected(p.id)} data-testid={`admin-project-row-${p.id}`}>
                  <td className="px-4 py-3 text-white font-medium">{p.name}</td>
                  <td className="px-4 py-3 text-slate-400">{p.account_email}</td>
                  <td className="px-4 py-3 text-slate-400 capitalize">{p.plan_id || '—'}</td>
                  <td className="px-4 py-3"><span className={`text-[11px] font-bold px-2 py-1 rounded ${STATE_TONE[p.lifecycle_state] || 'bg-slate-700 text-slate-300'}`}>{STATE_LABELS[p.lifecycle_state] || p.lifecycle_state}</span></td>
                  <td className="px-4 py-3 text-right"><ChevronRight className="w-4 h-4 text-slate-500 inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ProjectPipelineWrapper>
  );
};

const ProjectPipelineWrapper = ({ children }) => <div data-testid="project-pipeline-view">{children}</div>;
